/**
 * TanStack Query hooks for the Scribe User Memory feature.
 * BFF routes per contract: /api/memory/entries, /api/memory/entries/{id},
 */

import { useToast } from '@/hooks/use-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateMemoryEntryPayload,
  MemoryEntriesResponse,
  MemoryEntry,
  MemoryEntryResponse,
  UpdateMemoryEntryPayload,
} from './userMemory.types';

const MEMORY_ENTRIES_QUERY_KEY = ['memory-entries'] as const;

/**
 * Shared mutation `onError` handler: every memory mutation shows the same
 * generic error toast, so callers pass this instead of repeating it.
 */
export const useMemoryErrorToast = () => {
  const { toast } = useToast();
  return (error: Error) => toast({ title: 'Villa', description: error.message, variant: 'destructive' });
};

const parseErrorMessage = async (response: Response, fallback: string): Promise<string> => {
  try {
    const data = await response.json();
    return typeof data?.error === 'string' ? data.error : fallback;
  } catch {
    return fallback;
  }
};

const fetchMemoryEntries = async (status?: string): Promise<MemoryEntry[]> => {
  const query = status ? `?status=${encodeURIComponent(status)}` : '';
  const response = await fetch(`/api/memory/entries${query}`, {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(await parseErrorMessage(response, 'Ekki tókst að sækja minni.'));
  }
  const data: MemoryEntriesResponse = await response.json();
  return data.entries;
};

/** List memory entries, optionally filtered by CSV status (defaults to the BFF's suggested,active). */
export const useMemoryEntries = (status?: string) =>
  useQuery({
    queryKey: [...MEMORY_ENTRIES_QUERY_KEY, status ?? null],
    queryFn: () => fetchMemoryEntries(status),
  });

const createMemoryEntry = async (payload: CreateMemoryEntryPayload): Promise<MemoryEntry> => {
  const response = await fetch('/api/memory/entries', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(await parseErrorMessage(response, 'Ekki tókst að búa til minni.'));
  }
  const data: MemoryEntryResponse = await response.json();
  return data.entry;
};

export const useCreateMemoryEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createMemoryEntry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MEMORY_ENTRIES_QUERY_KEY });
    },
  });
};

interface UpdateMemoryEntryArgs {
  id: string;
  payload: UpdateMemoryEntryPayload;
}

const updateMemoryEntry = async ({ id, payload }: UpdateMemoryEntryArgs): Promise<MemoryEntry> => {
  const response = await fetch(`/api/memory/entries/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(await parseErrorMessage(response, 'Ekki tókst að uppfæra minni.'));
  }
  const data: MemoryEntryResponse = await response.json();
  return data.entry;
};

/** Covers accept (status: 'active'), reject (status: 'rejected'), retire (status: 'retired'), and text edit. */
export const useUpdateMemoryEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateMemoryEntry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MEMORY_ENTRIES_QUERY_KEY });
    },
  });
};

const deleteMemoryEntry = async (id: string): Promise<void> => {
  const response = await fetch(`/api/memory/entries/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok && response.status !== 204) {
    throw new Error(await parseErrorMessage(response, 'Ekki tókst að eyða minni.'));
  }
};

export const useDeleteMemoryEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteMemoryEntry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MEMORY_ENTRIES_QUERY_KEY });
    },
  });
};

interface TemplateOption {
  id: number;
  name: string;
}

const fetchTemplateOptions = async (): Promise<TemplateOption[]> => {
  const response = await fetch('/api/templates', {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(await parseErrorMessage(response, 'Ekki tókst að sækja sniðmát.'));
  }
  return response.json();
};

/**
 * Template options for the create-memory form's optional template Select.
 * Shares the `['templates']` query key with `pages/templates.tsx` so the two
 * views hit the same TanStack Query cache instead of double-fetching.
 */
export const useMemoryTemplateOptions = () =>
  useQuery({
    queryKey: ['templates'],
    queryFn: fetchTemplateOptions,
  });
